import { TextField } from '@/ui/form/controls/TextField'
import { Switch } from '@/ui/form/primitives/Switch'
import * as React from 'react'

import { useDriverForm } from '@/modules/drivers/context/DriverFormContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { DateField } from '@/ui/form/controls/DateField'
import { FieldsetSelect } from '@/modules/settings/components/FieldsetSelect'
import { Divider } from '../../shared/Divider'
import useDriverFieldSetOptions from '../options/useDriverFieldSetOptions'
import GroupsStage from './GroupsStage'

export const parsePesel = (pesel: string) => {
  if (!pesel || pesel.length !== 11) {
    return null
  }

  let year = parseInt(pesel.substring(0, 2), 10)
  let month = parseInt(pesel.substring(2, 4), 10)
  const day = parseInt(pesel.substring(4, 6), 10)

  if (month > 80) {
    year += 1800
    month -= 80
  } else if (month > 60) {
    year += 2200
    month -= 60
  } else if (month > 40) {
    year += 2100
    month -= 40
  } else if (month > 20) {
    year += 2000
    month -= 20
  } else {
    year += 1900
  }

  const genderDigit = parseInt(pesel.substring(9, 10), 10)
  const sex = genderDigit % 2 === 0 ? 1 : 0 

  const monthStr = month.toString().padStart(2, '0')
  const dayStr = day.toString().padStart(2, '0')
  const birthday = `${year}-${monthStr}-${dayStr}`

  const d = new Date(Date.UTC(year, month - 1, day))
  const isValidDate =
    d.getUTCFullYear() === year &&
    d.getUTCMonth() === month - 1 &&
    d.getUTCDate() === day

  return {
    birthday: isValidDate ? birthday : null,
    sex,
  }
}

const DriverDataStage = () => {
  const { t } = useLocalize()
  const { isActive, setIsActive, data, setData, countriesOptions, countries } =
    useDriverForm()
  const { driverSexOptions } = useDriverFieldSetOptions()

  const handlePeselChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pesel = e.target.value
    setData({ ...data, pesel })

    if (pesel.length === 11) {
      const parsed = parsePesel(pesel)
      if (parsed) {
        setData({
          ...data,
          pesel,
          sex: parsed.sex,
          ...(parsed.birthday ? { birthday: parsed.birthday } : {}),
        })
      }
    }
  }

  return (
    <div className="tw-flex tw-justify-between">
     <div className="tw-w-full tw-max-w-[256px]">
        <p className="tw-m-0 tw-text-lg tw-font-semibold tw-text-content-base">
          {t('driverBasics.driver-data')}
        </p>
      </div>
      <div className="tw-flex tw-flex-1 tw-flex-col tw-gap-4">
        <TextField
          label={t('driverBasics.first-name')}
          placeholder={t('common.type')}
          name="name"
          value={data?.name}
          onChange={(e) => setData({ ...data, name: e.target.value })}
        />
        <TextField
          label={t('driverBasics.last-name')}
          placeholder={t('common.type')}
          value={data?.surname}
          onChange={(e) => setData({ ...data, surname: e.target.value })}
        />
        <TextField
          label={t('driverBasics.pesel')}
          required
          placeholder={t('common.type')}
          value={data?.pesel || data?.driverId?.value || ''}
          onChange={handlePeselChange}
        />
        <DateField
          label={t('driverBasics.birth-date')}
          value={data?.birthday}
          setValue={(e) => setData({ ...data, birthday: e })}
        />
        <FieldsetSelect
          label={t('driverBasics.birth-country')}
          placeholder={t('common.select')}
          value={data?.birthCountry?.id?.toString() ?? ''}
          name={'birth_country'}
          onChange={(e) => {
            const id = e.target.value

            const countryObj = Array.isArray(countries)
              ? countries.find((c) => c.id.toString() === id)
              : null

            setData({
              ...data,
              birthCountry: countryObj ?? null,
            })
          }}
          options={countriesOptions}
        />
        <FieldsetSelect
          label={t('driverBasics.card-country')}
          placeholder={t('common.select')}
          value={data?.nationality?.id?.toString() ?? ''}
          name={'birth_country'}
          onChange={(e) => {
            const id = e.target.value

            const countryObj = Array.isArray(countries)
              ? countries.find((c) => c.id.toString() === id)
              : null

            setData({
              ...data,
              nationality: countryObj ?? null,
            })
          }}
          options={countriesOptions}
        />
        <FieldsetSelect
          label={t('driverBasics.sex')}
          options={driverSexOptions as any}
          value={data?.sex ?? ''}
          onChange={(e) => setData({ ...data, sex: Number(e.target.value) })}
        />
        <Divider />
        <div className="tw-flex tw-items-center tw-justify-between">
          <p className="tw-m-0 tw-text-sm tw-font-medium tw-text-content-base">
            {t('common.active')}
          </p>
          <Switch
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
        </div>
        <Divider />
        <GroupsStage />
      </div>
    </div>
  )
}

export default DriverDataStage
